export interface RunInfo {
  run_id: string
  model: string
  init_time: string | null
}

export interface Trace {
  run_id: string
  model: string
  init_time: string | null
  member_type: string
  member: number
  times: number[]   // epoch ms — the run's whole declared grid, from SSE init
  values: (number | null)[]   // dense over `times`; null where no value landed
}

// How a reading relates to the time it is filed under. Most params are a
// sample taken at that instant. A rate is instead the mean over the interval
// *ending* there, so the reading before it marks where its interval starts —
// which is why an absent leading step still has to keep its place in the grid.
export type Stamping = 'instant' | 'interval-end'

// Params served as a mean over the preceding interval rather than a sample.
// Note ``tp`` is not one of them: it is a running total, so its value at a
// valid time is a genuine reading at that instant.
const INTERVAL_END_PARAMS: ReadonlySet<string> = new Set(['avg_tprate'])

export function stampingFor(param: string): Stamping {
  return INTERVAL_END_PARAMS.has(param) ? 'interval-end' : 'instant'
}

export interface ParamData {
  param: string
  unit: string
  stamping: Stamping
  lat: number
  lon: number
  traces: Trace[]
}

// Chart composition: each tab is a centerpiece + an ordered list of
// strips rendered below. The chart code dispatches on these tagged
// unions rather than on a chartType enum, so new tabs are config-only.

export type Centerpiece =
  | { kind: 'filled-plume'; top: string; bot: string }   // t/Td meteogram-style
  | { kind: 'plume'; param: string }                      // single-param ensemble plume
  | { kind: 'precip'; param: string; rateParam: string }  // param = accumulated (mm), rateParam = rate (mm/h)

export type ScalarRamp = 'temp' | 'precip' | 'cloud' | 'wind' | 'wind-pl' | 'moisture'

export type Strip =
  | { kind: 'scalar'; param: string; ramp: ScalarRamp }
  | { kind: 'wind-rose'; u: string; v: string }

// A set of interchangeable params rendered by the *same* centerpiece +
// strips, switched by a header picker: "same chart, N params". Generic --
// any tab gains a param picker by declaring this, no per-param code path.
// ``options[0]`` is the default and must equal ``centerpiece.param`` so the
// config stays self-describing for code that reads it without resolving.
export interface ParamVariants {
  key: string                                    // URL/state key, e.g. 'feels_like'
  options: { value: string; label: string }[]   // param code + picker label
}

export interface ParamConfig {
  key: string
  label: string
  centerpiece: Centerpiece
  strips: Strip[]
  variants?: ParamVariants
}

export interface ModelGroup {
  label: string
  apiModels: string[]
  // Consecutive cycles to pool into one plume for a time-lagged ensemble
  // (MOGREPS-UK runs 3 members hourly, lagged over 6 h → 18 members).
  // Omitted/1 means a selection is just the one chosen cycle.
  lagWindow?: number
}

export const MODELS: ModelGroup[] = [
  { label: 'AIFS', apiModels: ['aifs'] },
  { label: 'IFS', apiModels: ['ifs'] },
  { label: 'ICON', apiModels: ['icon'] },
  { label: 'ICON-ART-EU', apiModels: ['icon-art-eu'] },
  { label: 'ICON-D2', apiModels: ['icon-d2'] },
  { label: 'ICON-D2-RUC', apiModels: ['icon-d2-ruc'] },
  { label: 'GEM', apiModels: ['gem'] },
  { label: 'UKMO-G', apiModels: ['ukmo-global'] },
  { label: 'UKMO-UK', apiModels: ['ukmo-uk'], lagWindow: 6 },
]

export const ORIGIN = location.hostname === 'localhost' ? 'https://wx.stephanie.is' : ''
export const API = `${ORIGIN}/api`

export type PrecipMode = 'rate' | 'accumulated'
export type WindUnit = 'm/s' | 'km/h' | 'kt' | 'mph'
export const WIND_UNITS: { key: WindUnit; label: string; factor: number }[] = [
  { key: 'm/s',  label: 'm/s',  factor: 1 },
  { key: 'km/h', label: 'km/h', factor: 3.6 },
  { key: 'kt',   label: 'kt',   factor: 1.94384 },
  { key: 'mph',  label: 'mph',  factor: 2.23694 },
]

export const MODEL_COLORS: Record<string, string> = {
  AIFS: '#2563eb',
  IFS: '#d97706',
  ICON: '#0891b2',          // cyan
  'ICON-D2': '#059669',
  'ICON-D2-RUC': '#7c3aed',
  'ICON-ART-EU': '#65a30d',     // lime/olive — green family with ICON-D2 but distinct
  GEM: '#b91c1c',           // deep red — maple-leaf nod
  'UKMO-G': '#c026d3',      // fuchsia — the open gap between RUC violet and the reds
  'UKMO-UK': '#db2777',     // rose — a Met Office sibling to UKMO-G fuchsia, one step warmer
}

export const PARAMS: ParamConfig[] = [
  {
    key: 'meteogram', label: 'Meteogram',
    centerpiece: { kind: 'filled-plume', top: '2t', bot: '2d' },
    strips: [
      { kind: 'scalar', param: '10si', ramp: 'wind' },
      { kind: 'scalar', param: 'avg_tprate', ramp: 'precip' },
      { kind: 'scalar', param: 'tcc',  ramp: 'cloud' },
    ],
  },
  { key: '2t',  label: '2m Temperature', centerpiece: { kind: 'plume', param: '2t' },  strips: [] },
  {
    key: 'feels-like', label: 'Feels Like',
    centerpiece: { kind: 'plume', param: 'utci' },   // = default (options[0])
    strips: [],
    variants: {
      key: 'feels_like',
      options: [
        { value: 'utci',       label: 'UTCI (sun)' },
        { value: 'utci_shade', label: 'UTCI (shade)' },
        { value: 'heatx',      label: 'Heat index' },
      ],
    },
  },
  { key: '2d',  label: '2m Dewpoint',    centerpiece: { kind: 'plume', param: '2d' },  strips: [] },
  {
    key: 'wind', label: '10m Wind',
    centerpiece: { kind: 'plume', param: '10si' },
    strips: [{ kind: 'wind-rose', u: '10u', v: '10v' }],
  },
  { key: 'tp',  label: 'Total Precipitation', centerpiece: { kind: 'precip', param: 'tp', rateParam: 'avg_tprate' }, strips: [] },
  { key: 'tcc', label: 'Total Cloud Cover',   centerpiece: { kind: 'plume', param: 'tcc' }, strips: [] },
  { key: 'synoptic', label: '850 hPa', centerpiece: { kind: 'plume', param: 't@850hPa' }, strips: [] },
]

export function hasWindData(config: ParamConfig): boolean {
  // The wind unit selector only changes the centerpiece's display
  // unit. Strips have their own internal km/h scale and don't react
  // to the selector — so a chart that only has wind in a strip
  // shouldn't surface the selector at all.
  return config.centerpiece.kind === 'plume' && isWindSpeedParam(config.centerpiece.param)
}

function isWindSpeedParam(p: string): boolean {
  // Surface ``10si`` and pressure-level ``ws@<L>hPa`` are the canonical
  // wind-speed codes; both display in m/s and want the unit selector.
  return p === '10si' || /^ws@\d+hPa$/.test(p)
}

export function paramsToFetch(config: ParamConfig): string[] {
  const params = new Set<string>()
  switch (config.centerpiece.kind) {
    case 'filled-plume':
      params.add(config.centerpiece.top)
      params.add(config.centerpiece.bot)
      break
    case 'plume':
    case 'precip':
      params.add(config.centerpiece.param)
      break
  }
  for (const strip of config.strips) {
    if (strip.kind === 'scalar') {
      params.add(strip.param)
    } else if (strip.kind === 'wind-rose') {
      params.add(strip.u)
      params.add(strip.v)
    }
  }
  return [...params]
}
